import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseReorderableOptions {
  /** 交换完成时回调，fromId 和 toId 均为上下文排序 ID，不一定是真实 edgeId。 */
  onReorder: (fromId: string, toId: string) => void
}

export interface UseReorderableReturn {
  /** 绑定到容器的 onPointerDown */
  handlePointerDown: (e: React.PointerEvent) => void
  /** 当前被拖拽项的 id，用于视觉反馈 */
  draggingId: string | null
  /** 当前悬停目标项的 id，用于视觉反馈 */
  targetId: string | null
  /** 拖拽时指针的 X 坐标（用于渲染浮动预览） */
  dragX: number | null
  /** 拖拽时指针的 Y 坐标（用于渲染浮动预览） */
  dragY: number | null
}

const REORDER_ID_ATTR = 'data-reorder-id'

export function useReorderable({ onReorder }: UseReorderableOptions): UseReorderableReturn {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [targetId, setTargetId] = useState<string | null>(null)
  const [dragX, setDragX] = useState<number | null>(null)
  const [dragY, setDragY] = useState<number | null>(null)

  // 使用 ref 避免事件回调中的闭包过期问题
  const draggingIdRef = useRef<string | null>(null)
  const targetIdRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)
  const onReorderRef = useRef(onReorder)

  useEffect(() => {
    // 拖拽事件监听挂在 document 上，回调用 ref 读取最新的排序处理函数。
    onReorderRef.current = onReorder
  }, [onReorder])

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // 仅左键拖拽
    if (e.button !== 0) return

    const clickedElement = e.target as HTMLElement
    // 点击按钮时不触发拖拽（删除按钮、引用按钮等）
    if (clickedElement.closest('button')) return

    const dragSource = clickedElement.closest(`[${REORDER_ID_ATTR}]`) as HTMLElement | null
    if (!dragSource) return

    e.preventDefault()

    const fromId = dragSource.getAttribute(REORDER_ID_ATTR)
    if (!fromId) return

    // 使用 currentTarget 作为容器（绑定了 onPointerDown 的元素），
    // 确保查询范围覆盖所有可拖拽项，而非仅限被拖拽项的父元素
    const container = e.currentTarget as HTMLElement
    if (!container) return
    containerRef.current = container

    draggingIdRef.current = fromId
    targetIdRef.current = null
    setDraggingId(fromId)
    setTargetId(null)
    setDragX(e.clientX)
    setDragY(e.clientY)

    // 缓存容器内所有可拖拽元素（排除被拖拽项自身）
    const buildCandidates = (): { id: string; el: HTMLElement }[] => {
      const items: { id: string; el: HTMLElement }[] = []
      const candidates = Array.from(container.querySelectorAll(`[${REORDER_ID_ATTR}]`))
      for (const candidate of candidates) {
        if (candidate instanceof HTMLElement) {
          const id = candidate.getAttribute(REORDER_ID_ATTR)
          if (id && id !== draggingIdRef.current) {
            items.push({ id, el: candidate })
          }
        }
      }
      return items
    }

    const candidates = buildCandidates()

    const onPointerMove = (moveEvent: PointerEvent) => {
      // 根据鼠标坐标找到当前悬停的项
      let hoveredId: string | null = null
      for (const { id, el } of candidates) {
        const rect = el.getBoundingClientRect()
        if (
          moveEvent.clientX >= rect.left &&
          moveEvent.clientX <= rect.right &&
          moveEvent.clientY >= rect.top &&
          moveEvent.clientY <= rect.bottom
        ) {
          hoveredId = id
          break
        }
      }
      targetIdRef.current = hoveredId
      setTargetId(hoveredId)
      setDragX(moveEvent.clientX)
      setDragY(moveEvent.clientY)
    }

    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)

      const from = draggingIdRef.current
      const to = targetIdRef.current

      setDraggingId(null)
      setTargetId(null)
      setDragX(null)
      setDragY(null)
      draggingIdRef.current = null
      targetIdRef.current = null
      containerRef.current = null

      if (from && to) {
        onReorderRef.current(from, to)
      }
    }

    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
  }, [])

  return { handlePointerDown, draggingId, targetId, dragX, dragY }
}
